import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { message, Table } from "antd";

import Title from "components/Title/Title";
import SectionCard from "components/SectionCard/SectionCard";
import SectionContainer from "components/SectionContainer/SectionContainer";
import AddEstatePlanningModal from "./components/AddEstatePlanningModal/AddEstatePlanningModal";
import EmergencyContingencyPlanningModal from "./components/EmergencyContingencyPlanningModal/EmergencyContingencyPlanningModal";
import AddEventModal from "./components/AddEventModal/AddEventModal";

import { patchAdviseeEstatePlanning } from "apis/advisee";
import Locale from "react-intl-universal";

const ESTATE_PLAN_TYPES = {
  ESTATE_PLANNINGS: "estatePlannings",
  EMERGENCY_CONTINGENCY_PLANNINGS: "emergencyContingencyPlannings",
  CONTINGENCY_PLANNING_FUNDING_EVENTS: "contingencyPlanningFundings",
};

const PRESET_SUGGESTIONS = {
  ESTATE_PLANNINGS: [
    "Having a Will?",
    "Executor appointment to Public Trustee?",
    "Appointed Guardian(s) for minor children?",
    "How many substitute layer?",
    "Have Residuary Clause?",
    "How many layer of beneficiary(ies)?",
    "Have Testamentary Trust?",
    "For Movable Properties?",
    "For Immovable Properties?",
  ],
  EMERGENCY_CONTINGENCY_PLANNINGS: [
    "Comatose",
    "Lost",
    "Death",
    "Legally incapacitated",
  ],
  CONTINGENCY_PLANNING_FUNDING_EVENTS: [
    "Legally Incapacitated / Comatose",
    "TPD",
    "Critically Ill",
    "Death",
    "Lost",
    "Retirement",
  ],
};

const formatPresetSuggestions = (presets) =>
  presets.map((preset) => ({ label: preset, value: preset }));

const Summary = ({ estatePlanningDetails = {} }) => {
  const estatePlanningColumns = [
    {
      title: Locale.get("estateContingencyPlan.plan"),
      dataIndex: "plan",
      key: "plan",
      width: "35%",
    },
    {
      title: Locale.get("estateContingencyPlan.interpretation"),
      dataIndex: "interpretation",
      key: "interpretation",
      width: "30%",
    },
    {
      title: Locale.get("estateContingencyPlan.notes"),
      dataIndex: "remarks",
      key: "remarks",
    },
  ];

  const emergencyContingencyPlanningColumns = [
    {
      title: Locale.get("estateContingencyPlan.plan"),
      dataIndex: "plan",
      key: "plan",
      width: "35%",
    },
    {
      title: Locale.get("estateContingencyPlan.flowOfFunds"),
      dataIndex: "flowOfFundsViaDeclarationTrust",
      key: "flowOfFundsViaDeclarationTrust",
      width: "30%",
    },
    {
      title: Locale.get("estateContingencyPlan.notes"),
      dataIndex: "remarks",
      key: "remarks",
    },
  ];

  const fundingEventColumns = [
    {
      title: Locale.get("estateContingencyPlan.eventTitle"),
      dataIndex: "event",
      key: "event",
    },
    {
      title: Locale.get("estateContingencyPlan.solution"),
      dataIndex: "solution",
      key: "solution",
    },
    {
      title: Locale.get("estateContingencyPlan.toDateStatus"),
      dataIndex: "toDateStatus",
      key: "toDateStatus",
    },
    {
      title: Locale.get("estateContingencyPlan.detail"),
      dataIndex: "remarks",
      key: "remarks",
    },
  ];

  const { id: adviseeId } = useParams();

  const [showEstatePlanningModal, setShowEstatePlanningModal] = useState(false);
  const [
    showEmergencyContingencyPlanningModal,
    setShowEmergencyContingencyPlanningModal,
  ] = useState(false);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(-1);
  const [selectedEstateType, setSelectedEstateType] = useState("");

  const updateEstatePlanning = (
    estatePlan,
    successMessage = Locale.get("estateContingencyPlan.updateSuccess")
  ) => {
    patchAdviseeEstatePlanning(adviseeId, selectedEstateType, estatePlan)
      .then((res) => {
        message.success(successMessage);
        setModalVisibility(false);
        setSelectedPlanIndex(-1);
        setSelectedEstateType("");
      })
      .catch((ex) => {
        message.error(
          "Error in updating estate planning details. Please check your connection and try again."
        );
      });
  };

  const handleOnRowSelect = (index, type) => {
    setSelectedPlanIndex(index);
    setSelectedEstateType(type);
    setModalVisibility(true, type);
  };

  const handleOnModalOkButtonClick = (values) => {
    if (selectedPlanIndex > -1) {
      let editedPlans = [...estatePlanningDetails[selectedEstateType]];
      editedPlans[selectedPlanIndex] = values;
      updateEstatePlanning(editedPlans);
    } else {
      const initialEstatePlanByType =
        estatePlanningDetails[selectedEstateType] || [];
      const newPlans = [...initialEstatePlanByType, values];
      updateEstatePlanning(
        newPlans,
        Locale.get("estateContingencyPlan.addSuccessMsg")
      );
    }
  };

  const handleOnModalRemoveButtonClick = () => {
    const newPlans = [...estatePlanningDetails[selectedEstateType]];
    newPlans.splice(selectedPlanIndex, 1);
    updateEstatePlanning(
      newPlans,
      Locale.get("estateContingencyPlan.removeSuccessMsg")
    );
  };

  const handleOnModalCancelButtonClick = () => {
    setModalVisibility(false);
    setSelectedPlanIndex(-1);
    setSelectedEstateType("");
  };

  const setModalVisibility = (visible, type) => {
    switch (type || selectedEstateType) {
      case ESTATE_PLAN_TYPES.ESTATE_PLANNINGS:
        setShowEstatePlanningModal(visible);
        break;
      case ESTATE_PLAN_TYPES.EMERGENCY_CONTINGENCY_PLANNINGS:
        setShowEmergencyContingencyPlanningModal(visible);
        break;
      default:
        setShowEventsModal(visible);
        break;
    }
  };

  return (
    <>
      <Title marginBottom="8px">
        {Locale.get("estateContingencyPlan.titleEstateContingencyPlan")}
      </Title>
      <SectionContainer>
        <SectionCard
          title={Locale.get("estateContingencyPlan.estatePlanning")}
          buttonText={Locale.get("estateContingencyPlan.addNewPlan")}
          onActionButtonClick={() => {
            setShowEstatePlanningModal(true);
            setSelectedEstateType(ESTATE_PLAN_TYPES.ESTATE_PLANNINGS);
          }}
        >
          <Table
            dataSource={
              estatePlanningDetails[ESTATE_PLAN_TYPES.ESTATE_PLANNINGS]
            }
            columns={estatePlanningColumns}
            pagination={false}
            rowKey={(record) => record.plan + record.interpretation}
            style={{ width: "100%" }}
            onRow={(record, index) => ({
              onClick: () =>
                handleOnRowSelect(index, ESTATE_PLAN_TYPES.ESTATE_PLANNINGS),
            })}
          />
        </SectionCard>
      </SectionContainer>
      <SectionContainer>
        <SectionCard
          title={Locale.get(
            "estateContingencyPlan.expressEmergencyContingencyPlan"
          )}
          buttonText={Locale.get("estateContingencyPlan.addNewPlan")}
          onActionButtonClick={() => {
            setShowEmergencyContingencyPlanningModal(true);
            setSelectedEstateType(
              ESTATE_PLAN_TYPES.EMERGENCY_CONTINGENCY_PLANNINGS
            );
          }}
        >
          <Table
            dataSource={
              estatePlanningDetails[
                ESTATE_PLAN_TYPES.EMERGENCY_CONTINGENCY_PLANNINGS
              ]
            }
            columns={emergencyContingencyPlanningColumns}
            pagination={false}
            rowKey={(record) =>
              record.plan + record.flowOfFundsViaDeclarationTrust
            }
            style={{ width: "100%" }}
            onRow={(record, index) => ({
              onClick: () =>
                handleOnRowSelect(
                  index,
                  ESTATE_PLAN_TYPES.EMERGENCY_CONTINGENCY_PLANNINGS
                ),
            })}
          />
        </SectionCard>
      </SectionContainer>
      <Title marginBottom="8px">
        {Locale.get("estateContingencyPlan.ffcp")}
      </Title>
      <SectionContainer>
        <SectionCard
          title={Locale.get("estateContingencyPlan.eventTitle")}
          buttonText={Locale.get("estateContingencyPlan.addEvent")}
          onActionButtonClick={() => {
            setShowEventsModal(true);
            setSelectedEstateType(
              ESTATE_PLAN_TYPES.CONTINGENCY_PLANNING_FUNDING_EVENTS
            );
          }}
        >
          <Table
            dataSource={
              estatePlanningDetails[
                ESTATE_PLAN_TYPES.CONTINGENCY_PLANNING_FUNDING_EVENTS
              ]
            }
            columns={fundingEventColumns}
            pagination={false}
            rowKey={(record) =>
              record.event + record.solution + record.toDateStatus
            }
            style={{ width: "100%" }}
            onRow={(record, index) => ({
              onClick: () =>
                handleOnRowSelect(
                  index,
                  ESTATE_PLAN_TYPES.CONTINGENCY_PLANNING_FUNDING_EVENTS
                ),
            })}
          />
        </SectionCard>
      </SectionContainer>

      {showEstatePlanningModal && (
        <AddEstatePlanningModal
          defaultValues={
            selectedPlanIndex > -1 &&
            estatePlanningDetails[selectedEstateType][selectedPlanIndex]
          }
          onOk={handleOnModalOkButtonClick}
          onClose={handleOnModalCancelButtonClick}
          onRemove={handleOnModalRemoveButtonClick}
          suggestions={formatPresetSuggestions(
            PRESET_SUGGESTIONS.ESTATE_PLANNINGS
          )}
          visible={showEstatePlanningModal}
        />
      )}
      {showEmergencyContingencyPlanningModal && (
        <EmergencyContingencyPlanningModal
          defaultValues={
            selectedPlanIndex > -1 &&
            estatePlanningDetails[selectedEstateType][selectedPlanIndex]
          }
          onOk={handleOnModalOkButtonClick}
          onClose={handleOnModalCancelButtonClick}
          onRemove={handleOnModalRemoveButtonClick}
          suggestions={formatPresetSuggestions(
            PRESET_SUGGESTIONS.EMERGENCY_CONTINGENCY_PLANNINGS
          )}
          visible={showEmergencyContingencyPlanningModal}
        />
      )}
      {showEventsModal && (
        <AddEventModal
          defaultValues={
            selectedPlanIndex > -1 &&
            estatePlanningDetails[selectedEstateType][selectedPlanIndex]
          }
          onOk={handleOnModalOkButtonClick}
          onClose={handleOnModalCancelButtonClick}
          onRemove={handleOnModalRemoveButtonClick}
          suggestions={formatPresetSuggestions(
            PRESET_SUGGESTIONS.CONTINGENCY_PLANNING_FUNDING_EVENTS
          )}
          visible={showEventsModal}
        />
      )}
    </>
  );
};

export default Summary;
